import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Copy } from "lucide-react";
import { ApiError } from "@finora/api-client";
import { Button } from "@finora/ui";
import { api } from "../../lib/api";
import { ModuleGateNotice } from "../../components/ModuleGateNotice";

function toIsoDate(d: Date): string {
  return d.toISOString().slice(0, 10);
}
function yesterday(): string {
  const d = new Date();
  d.setDate(d.getDate() - 1);
  return toIsoDate(d);
}

export function MinimaxTab() {
  const queryClient = useQueryClient();
  const {
    data: status,
    isLoading,
    isError: isStatusError,
    error: statusError,
  } = useQuery({
    queryKey: ["minimax-status"],
    queryFn: () => api.getMinimaxStatus(),
    // Ako modul "minimax_api" nije aktivan za firmu (RequireModule guard na
    // backendu vraća 403), nema smisla ponavljati zahtev - status i dalje
    // neće proći dok admin ne upali modul. Bez ovoga stranica ostaje
    // zaglavljena na "Učitavanje..." zauvek (vidi ispod: !status grana).
    retry: false,
  });

  const invalidate = () => queryClient.invalidateQueries({ queryKey: ["minimax-status"] });

  // Samo za PIB (prikaz stvarne adrese za uvoz izvoda ispod) - 04.10.2026.
  const { data: companySettings } = useQuery({
    queryKey: ["company-settings"],
    queryFn: () => api.getCompanySettings(),
  });
  const importEmail = companySettings?.taxId ? `obradaizvoda_${companySettings.taxId}@import.hafiskal.rs` : null;

  const [statementEmail, setStatementEmail] = useState<string | null>(null);
  const saveStatementEmail = useMutation({
    mutationFn: () => api.saveMinimaxStatementEmail(statementEmail ?? status?.statementEmail ?? ""),
    onSuccess: invalidate,
  });

  const [clientId, setClientId] = useState("");
  const [clientSecret, setClientSecret] = useState("");
  const saveCredentials = useMutation({
    mutationFn: () => api.saveMinimaxCredentials(clientId, clientSecret),
    onSuccess: () => {
      invalidate();
      setClientId("");
      setClientSecret("");
    },
  });

  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const connect = useMutation({
    mutationFn: () => api.connectMinimax(username, password),
    onSuccess: () => {
      invalidate();
      setUsername("");
      setPassword("");
    },
  });

  const { data: organisations } = useQuery({
    queryKey: ["minimax-organisations"],
    queryFn: () => api.listMinimaxOrganisations(),
    enabled: !!status?.connected && !status?.organisationId,
  });
  const selectOrg = useMutation({
    mutationFn: (org: { ID: number; Name: string }) =>
      api.selectMinimaxOrganisation(String(org.ID), org.Name),
    onSuccess: invalidate,
  });

  const { data: warehouses } = useQuery({
    queryKey: ["minimax-warehouses"],
    queryFn: () => api.listMinimaxWarehouses(),
    enabled: !!status?.organisationId && !status?.warehouseId,
  });
  const selectWarehouse = useMutation({
    mutationFn: (w: { WarehouseId: number; Name: string }) =>
      api.selectMinimaxWarehouse(String(w.WarehouseId), w.Name),
    onSuccess: invalidate,
  });

  const [cashId, setCashId] = useState("");
  const [cardId, setCardId] = useState("");
  const [vatType, setVatType] = useState("");
  const { data: paymentMethods, isError: paymentMethodsError } = useQuery({
    queryKey: ["minimax-payment-methods"],
    queryFn: () => api.listMinimaxPaymentMethods(),
    enabled: !!status?.organisationId,
    retry: false,
  });
  const { data: vatAccountingTypes, isError: vatAccountingTypesError } = useQuery({
    queryKey: ["minimax-vat-accounting-types"],
    queryFn: () => api.listMinimaxVatAccountingTypes(),
    enabled: !!status?.organisationId,
    retry: false,
  });
  const savePostingSettings = useMutation({
    mutationFn: () =>
      api.saveMinimaxPostingSettings({
        cashPaymentMethodId: cashId || undefined,
        cardPaymentMethodId: cardId || undefined,
        vatCalculationType: vatType || undefined,
      }),
    onSuccess: invalidate,
  });

  const [reportDate, setReportDate] = useState(yesterday());
  const sendStockReport = useMutation({
    mutationFn: () => api.sendMinimaxDailyReport(reportDate),
    onSuccess: invalidate,
  });
  const sendInvoicePosting = useMutation({
    mutationFn: () => api.sendMinimaxDailyInvoicePosting(reportDate),
    onSuccess: invalidate,
  });
  const syncItems = useMutation({
    mutationFn: () => api.syncMinimaxItems(),
  });
  const disconnect = useMutation({
    mutationFn: () => api.disconnectMinimax(),
    onSuccess: invalidate,
  });

  const clearCredentials = useMutation({
    mutationFn: () => api.clearMinimaxCredentials(),
    onSuccess: invalidate,
  });

  if (isLoading) {
    return <p className="text-sm text-slate-500">Učitavanje...</p>;
  }

  // RANIJE se ovde vraćalo "Učitavanje..." i za grešku (npr. 403 kad modul
  // "minimax_api" nije aktivan za firmu) - status je tada ostajao trajno
  // undefined i stranica se doživotno vrtela u loading stanju bez ijedne
  // poruke (vidi razgovor "vrti kao blesav a ne pokazuje nista"). Sada se
  // stvarna backend poruka (npr. "Modul ... nije aktivan...") prikazuje.
  if (isStatusError || !status) {
    // Ako je greška "modul nije uključen u paket" (vidi EntitlementService),
    // nudi "Aktiviraj dodatak" CTA umesto sirove poruke - vidi razgovor
    // "generic CTA za modul nije uključen".
    if (statusError instanceof ApiError && statusError.response.errorCode === "MODULE_NOT_AVAILABLE") {
      return <ModuleGateNotice error={statusError} moduleKey="minimax_api" />;
    }
    return (
      <p className="rounded-lg bg-red-50 px-3 py-2 text-sm text-red-700">
        {statusError instanceof ApiError
          ? statusError.response.message
          : "Učitavanje MiniMax podešavanja nije uspelo. Osvežite stranicu ili pokušajte kasnije."}
      </p>
    );
  }

  return (
    <div className="space-y-5">
      <div className="flex items-start justify-between">
        <div>
          <h2 className="text-sm font-semibold text-slate-800">MiniMax integracija</h2>
          <p className="text-xs text-slate-400">
            Svaka firma registruje sopstvenu aplikaciju kod MiniMax-a. Status:{" "}
            <span className="font-medium">{status.status ?? "Nepovezano"}</span>
            {status.lastError && <span className="text-red-600"> · {status.lastError}</span>}
          </p>
        </div>
        {(status.hasClientId || status.hasClientSecret) && (
          <button
            onClick={() => {
              if (window.confirm("Obrisati Client ID/Secret i sve podatke o konekciji? Kreneš ispočetka od Koraka 1.")) {
                clearCredentials.mutate();
              }
            }}
            disabled={clearCredentials.isPending}
            className="shrink-0 text-xs text-red-500 hover:text-red-700 hover:underline"
          >
            Obriši kredencijale
          </button>
        )}
      </div>

      <div className="space-y-2 rounded-lg border border-border p-3">
        <p className="text-xs font-medium text-slate-500">Email za izvode (MiniMax)</p>
        <p className="text-xs text-slate-400">
          Na ovu adresu HAFiskal šalje obrađene bankovne izvode (XML) za uvoz u MiniMax. Izvode
          šaljete na adresu ispod - radi nezavisno od koraka povezivanja ispod, ali zahteva bar
          jedan bankovni račun u Podešavanja → Firma → Bankovni računi.
        </p>
        {importEmail && (
          <div className="flex items-center gap-1.5 text-xs text-slate-600">
            <span className="font-mono font-medium text-slate-800">{importEmail}</span>
            <button
              type="button"
              onClick={() => navigator.clipboard.writeText(importEmail).catch(() => undefined)}
              className="text-slate-400 hover:text-brand"
              title="Kopiraj"
            >
              <Copy className="h-3.5 w-3.5" aria-hidden />
            </button>
          </div>
        )}
        <div className="flex gap-2">
          <input
            value={statementEmail ?? status.statementEmail ?? ""}
            onChange={(e) => setStatementEmail(e.target.value)}
            placeholder="npr. vasafirma@email.minimax.rs"
            type="email"
            className="h-9 flex-1 rounded-lg border border-border px-2 text-sm focus:border-brand focus:outline-none"
          />
          <Button
            size="sm"
            disabled={saveStatementEmail.isPending || statementEmail === null}
            onClick={() => saveStatementEmail.mutate()}
          >
            Sačuvaj
          </Button>
        </div>
        {saveStatementEmail.isError && (
          <p className="text-xs text-red-600">
            {saveStatementEmail.error instanceof ApiError
              ? saveStatementEmail.error.response.message
              : "Čuvanje nije uspelo."}
          </p>
        )}
      </div>

      {!status.hasClientId || !status.hasClientSecret ? (
        <div className="space-y-2 rounded-lg border border-border p-3">
          <p className="text-xs font-medium text-slate-500">
            Korak 1 — unesi Client ID/Secret svoje MiniMax aplikacije
          </p>
          <div className="flex gap-2">
            <input
              value={clientId}
              onChange={(e) => setClientId(e.target.value)}
              placeholder="Client ID"
              className="h-9 flex-1 rounded-lg border border-border px-2 text-sm focus:border-brand focus:outline-none"
            />
            <input
              value={clientSecret}
              onChange={(e) => setClientSecret(e.target.value)}
              placeholder="Client Secret"
              type="password"
              className="h-9 flex-1 rounded-lg border border-border px-2 text-sm focus:border-brand focus:outline-none"
            />
          </div>
          <Button
            size="sm"
            disabled={!clientId || !clientSecret || saveCredentials.isPending}
            onClick={() => saveCredentials.mutate()}
          >
            Sačuvaj
          </Button>
        </div>
      ) : !status.connected ? (
        <div className="space-y-2 rounded-lg border border-border p-3">
          <p className="text-xs font-medium text-slate-500">
            Korak 2 — poveži se MiniMax korisničkim imenom/lozinkom
          </p>
          <div className="flex gap-2">
            <input
              value={username}
              onChange={(e) => setUsername(e.target.value)}
              placeholder="Korisničko ime"
              className="h-9 flex-1 rounded-lg border border-border px-2 text-sm focus:border-brand focus:outline-none"
            />
            <input
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder="Lozinka"
              type="password"
              className="h-9 flex-1 rounded-lg border border-border px-2 text-sm focus:border-brand focus:outline-none"
            />
          </div>
          {connect.isError && (
            <p className="text-xs text-red-600">
              {connect.error instanceof Error ? connect.error.message : "Povezivanje nije uspelo."}
            </p>
          )}
          <Button size="sm" disabled={!username || !password || connect.isPending} onClick={() => connect.mutate()}>
            Poveži se
          </Button>
        </div>
      ) : !status.organisationId ? (
        <div className="space-y-2 rounded-lg border border-border p-3">
          <p className="text-xs font-medium text-slate-500">Korak 3 — izaberi organizaciju</p>
          {organisations && organisations.length === 0 && (
            <p className="rounded-lg bg-amber-50 px-3 py-2 text-xs text-amber-700">
              Nijedna organizacija nije pronađena za ovaj MiniMax nalog - proveri da li je korisnik
              kojim si se povezao stvarno dodat kao korisnik te organizacije u MiniMax-u.
            </p>
          )}
          {organisations?.map((org) => (
            <button
              key={org.ID}
              onClick={() => selectOrg.mutate(org)}
              className="block w-full rounded-lg border border-border px-3 py-2 text-left text-sm hover:bg-app-bg"
            >
              {org.Name}
            </button>
          ))}
        </div>
      ) : !status.warehouseId ? (
        <div className="space-y-2 rounded-lg border border-border p-3">
          <p className="text-xs font-medium text-slate-500">Korak 4 — izaberi skladište</p>
          {warehouses && warehouses.length === 0 && (
            <p className="rounded-lg bg-amber-50 px-3 py-2 text-xs text-amber-700">
              Nema nijednog skladišta u MiniMax-u za ovu organizaciju. Idi u sam MiniMax:
              Šifarnici → Skladišta → Novo, napravi bar jedno, pa se vrati ovde.
            </p>
          )}
          {warehouses?.map((w) => (
            <button
              key={w.WarehouseId}
              onClick={() => selectWarehouse.mutate(w)}
              className="block w-full rounded-lg border border-border px-3 py-2 text-left text-sm hover:bg-app-bg"
            >
              {w.Name}
            </button>
          ))}
        </div>
      ) : (
        <>
          <div className="rounded-lg bg-emerald-50 px-3 py-2 text-sm text-emerald-700">
            Povezano — {status.organisationName} · Skladište: {status.warehouseName}
          </div>

          <div className="rounded-lg border border-border p-3">
            <div className="mb-2 flex items-center justify-between">
              <p className="text-xs font-medium text-slate-500">Artikli</p>
              <Button size="sm" variant="secondary" disabled={syncItems.isPending} onClick={() => syncItems.mutate()}>
                Sinhronizuj artikle
              </Button>
            </div>
            {status.itemsLastSyncedAt && (
              <p className="text-xs text-slate-400">
                Poslednja sinhronizacija: {new Date(status.itemsLastSyncedAt).toLocaleString("sr-RS")}
              </p>
            )}
            {syncItems.data && (
              <p className="mt-1 text-xs text-slate-500">
                Kreirano: {syncItems.data.created} · Ažurirano: {syncItems.data.updated} · Preskočeno:{" "}
                {syncItems.data.skipped}
              </p>
            )}
          </div>

          <div className="space-y-2 rounded-lg border border-border p-3">
            <p className="text-xs font-medium text-slate-500">
              Podešavanja za knjiženje dnevnog izveštaja (IssuedInvoicePosting)
            </p>
            <p className="text-xs text-slate-400">
              "Vrsta obračuna PDV" je šifra iz MiniMax šifarnika koja se vidi i na tvom postojećem
              dnevnom izveštaju u MiniMax-u (npr. "3.2").
            </p>
            {paymentMethodsError && (
              <p className="rounded-lg bg-amber-50 px-3 py-2 text-xs text-amber-700">
                Nije uspelo automatsko učitavanje načina plaćanja iz MiniMax-a - unesi ID ručno (nalazi
                se u MiniMax-u: Šifarnici → Način plaćanja).
              </p>
            )}
            <div className="grid grid-cols-3 gap-2">
              {paymentMethods && paymentMethods.length > 0 ? (
                <>
                  <select
                    defaultValue={status.cashPaymentMethodId ?? ""}
                    onChange={(e) => setCashId(e.target.value)}
                    className="h-9 rounded-lg border border-border px-2 text-sm focus:border-brand focus:outline-none"
                  >
                    <option value="">Gotovina - izaberi...</option>
                    {paymentMethods.map((pm) => (
                      <option key={pm.ID} value={pm.ID}>
                        {pm.Name}
                        {pm.Type ? ` (${pm.Type})` : ""}
                      </option>
                    ))}
                  </select>
                  <select
                    defaultValue={status.cardPaymentMethodId ?? ""}
                    onChange={(e) => setCardId(e.target.value)}
                    className="h-9 rounded-lg border border-border px-2 text-sm focus:border-brand focus:outline-none"
                  >
                    <option value="">Kartica - izaberi...</option>
                    {paymentMethods.map((pm) => (
                      <option key={pm.ID} value={pm.ID}>
                        {pm.Name}
                        {pm.Type ? ` (${pm.Type})` : ""}
                      </option>
                    ))}
                  </select>
                </>
              ) : (
                <>
                  <input
                    defaultValue={status.cashPaymentMethodId ?? ""}
                    onChange={(e) => setCashId(e.target.value)}
                    placeholder="ID - Gotovina"
                    className="h-9 rounded-lg border border-border px-2 text-sm focus:border-brand focus:outline-none"
                  />
                  <input
                    defaultValue={status.cardPaymentMethodId ?? ""}
                    onChange={(e) => setCardId(e.target.value)}
                    placeholder="ID - Kartica"
                    className="h-9 rounded-lg border border-border px-2 text-sm focus:border-brand focus:outline-none"
                  />
                </>
              )}
              {vatAccountingTypes && vatAccountingTypes.length > 0 ? (
                <select
                  defaultValue={status.vatCalculationType ?? ""}
                  onChange={(e) => setVatType(e.target.value)}
                  className="h-9 rounded-lg border border-border px-2 text-sm focus:border-brand focus:outline-none"
                >
                  <option value="">Vrsta obračuna PDV - izaberi...</option>
                  {vatAccountingTypes.map((v) => (
                    <option key={v.Code} value={v.Code}>
                      {v.Code} - {v.Name}
                      {v.IssuedRecived ? ` (${v.IssuedRecived})` : ""}
                    </option>
                  ))}
                </select>
              ) : (
                <input
                  defaultValue={status.vatCalculationType ?? ""}
                  onChange={(e) => setVatType(e.target.value)}
                  placeholder='Vrsta obračuna PDV (npr. "3.2")'
                  className="h-9 rounded-lg border border-border px-2 text-sm focus:border-brand focus:outline-none"
                />
              )}
            </div>
            {vatAccountingTypesError && (
              <p className="rounded-lg bg-amber-50 px-3 py-2 text-xs text-amber-700">
                Nije uspelo automatsko učitavanje vrsta obračuna PDV iz MiniMax-a - unesi šifru ručno
                (vidi je na postojećem dnevnom izveštaju u MiniMax-u).
              </p>
            )}
            <Button size="sm" disabled={savePostingSettings.isPending} onClick={() => savePostingSettings.mutate()}>
              Sačuvaj
            </Button>
          </div>

          <div className="space-y-2 rounded-lg border border-border p-3">
            <p className="text-xs font-medium text-slate-500">Ručno slanje dnevnog izveštaja</p>
            <input
              type="date"
              value={reportDate}
              onChange={(e) => setReportDate(e.target.value)}
              className="h-9 rounded-lg border border-border px-2 text-sm focus:border-brand focus:outline-none"
            />
            <div className="flex flex-wrap gap-2">
              <Button size="sm" variant="secondary" disabled={sendStockReport.isPending} onClick={() => sendStockReport.mutate()}>
                Pošalji promet zaliha (StockEntry)
              </Button>
              <Button
                size="sm"
                variant="secondary"
                disabled={sendInvoicePosting.isPending}
                onClick={() => sendInvoicePosting.mutate()}
              >
                Knjiži dnevni izveštaj (IssuedInvoicePosting)
              </Button>
            </div>
            {sendStockReport.isError && (
              <p className="text-xs text-red-600">
                {sendStockReport.error instanceof Error ? sendStockReport.error.message : "Slanje nije uspelo."}
              </p>
            )}
            {sendInvoicePosting.isError && (
              <p className="text-xs text-red-600">
                {sendInvoicePosting.error instanceof Error
                  ? sendInvoicePosting.error.message
                  : "Knjiženje nije uspelo."}
              </p>
            )}
            {status.lastReport && (
              <p className="text-xs text-slate-500">
                Poslednji izveštaj ({status.lastReport.reportDate}): promet zaliha —{" "}
                {status.lastReport.status}
                {status.lastReport.lastError && (
                  // Razgovor 23.09.2026: "ne znamo o čemu se radi Preskočeno
                  // X grešaka" - privremena ([DEBUG]) dijagnostika iz
                  // backend-a prikazana amber, prava greška slanja crveno -
                  // vidi isti obrazac u MinimaxReportsTab.tsx.
                  <span className={status.lastReport.lastError.startsWith("[DEBUG]") ? "text-amber-700" : "text-red-600"}>
                    {" "}
                    ({status.lastReport.lastError})
                  </span>
                )}
                {", knjiženje — "}
                {status.lastReport.postingStatus ?? "—"}
                {status.lastReport.postingLastError && (
                  <span
                    className={
                      status.lastReport.postingLastError.startsWith("[DEBUG]") ? "text-amber-700" : "text-red-600"
                    }
                  >
                    {" "}
                    ({status.lastReport.postingLastError})
                  </span>
                )}
              </p>
            )}
          </div>

          <Button size="sm" variant="secondary" disabled={disconnect.isPending} onClick={() => disconnect.mutate()}>
            Prekini konekciju
          </Button>
        </>
      )}
    </div>
  );
}
